//#region IMPORT
import React from 'react';
import {
  Unstable_Grid2 as Grid,
  Box,
  Paper,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Button } from '../../../../Components/MUIs';
import { SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import {
  DAT_BASIC_MIGRATION_MODE,
  DAT_BASIC_MIGRATION_STATUS,
  DAT_BASIC_MIGRATION_STATUS_LABEL,
  DAT_BASIC_MIGRATION_EXCLUDED_PAGES,
  GetMigratableGuideRows,
  GetMigratableMigrationPages,
  GetSelectedMigrationGuide,
  GetMigrationTabOptions,
  HasMigrationTabs,
} from './DAT_BASIC_MigrationPages';
//#endregion

function DAT_BASIC(props) {
  const { inputs } = props;
  const isPageMode = inputs.migrationMode === DAT_BASIC_MIGRATION_MODE.PAGE;
  const tabOptions = isPageMode ? GetMigrationTabOptions(inputs.proId) : [];
  const showTabSelect = isPageMode && HasMigrationTabs(inputs.proId);
  const showSamDepMapping = props.showSamDepMapping;
  const migratablePages = GetMigratableMigrationPages();
  const migratableGuideRows = GetMigratableGuideRows();
  const selectedGuide = isPageMode
    ? GetSelectedMigrationGuide(inputs.proId, inputs.tabKey ?? '')
    : null;

  const getStatusChipProps = (status) => {
    switch (status) {
      case DAT_BASIC_MIGRATION_STATUS.DEP_MAPPING:
        return { label: DAT_BASIC_MIGRATION_STATUS_LABEL.DEP_MAPPING, color: 'primary' };
      case DAT_BASIC_MIGRATION_STATUS.EXCLUDED:
        return { label: DAT_BASIC_MIGRATION_STATUS_LABEL.EXCLUDED, color: 'error' };
      default:
        return { label: DAT_BASIC_MIGRATION_STATUS_LABEL.AVAILABLE, color: 'success' };
    }
  };

  const renderMigrationGuidePanel = () => (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2.5,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        bgcolor: '#fff',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        이관 안내 (관리자 확인)
      </Typography>

      {selectedGuide ? (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 1.5,
            bgcolor:
              selectedGuide.status === DAT_BASIC_MIGRATION_STATUS.DEP_MAPPING
                ? '#e3f2fd'
                : '#f1f8e9',
            border: '1px solid',
            borderColor:
              selectedGuide.status === DAT_BASIC_MIGRATION_STATUS.DEP_MAPPING
                ? '#90caf9'
                : '#c5e1a5',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip size="small" {...getStatusChipProps(selectedGuide.status)} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {selectedGuide.menuName}
              {selectedGuide.tabName ? ` · ${selectedGuide.tabName}` : ''}
            </Typography>
          </Box>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {selectedGuide.bullets.map((line) => (
              <Typography
                key={line}
                component="li"
                variant="body2"
                sx={{ color: '#333', mb: 0.5 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: '#5f6368', mb: 2.5 }}>
          이관할 페이지(및 탭)를 선택하면 해당 메뉴의 이관 방법이 표시됩니다.
        </Typography>
      )}

      <Grid container spacing={2} columns={{ xs: 1, md: 2 }}>
        <Grid xs={1} md={1}>
          <Box
            sx={{
              height: '100%',
              p: 2,
              borderRadius: 1.5,
              border: '1px solid #c5e1a5',
              bgcolor: '#f9fbe7',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#2e7d32' }}>
              이관 가능 메뉴
            </Typography>
            <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', mb: 1.5 }}>
              본 화면에서 원본·대상 회사·사업장을 선택해 복사할 수 있습니다.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {migratableGuideRows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    border: '1px solid #e8f5e9',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.5,
                    }}
                  >
                    <Chip size="small" {...getStatusChipProps(row.status)} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.menuName}
                      {row.tabName ? ` · ${row.tabName}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#5f6368', fontSize: 13 }}>
                    {row.note}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid xs={1} md={1}>
          <Box
            sx={{
              height: '100%',
              p: 2,
              borderRadius: 1.5,
              border: '1px solid #ef9a9a',
              bgcolor: '#fff5f5',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#c62828' }}>
              이관 불가 메뉴
            </Typography>
            <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', mb: 1.5 }}>
              부서·인력·공정 코드가 필요합니다. 해당 메뉴 화면에서 직접 등록하세요.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {DAT_BASIC_MIGRATION_EXCLUDED_PAGES.map((row) => (
                <Box
                  key={row.value}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    border: '1px solid #ffcdd2',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.5,
                    }}
                  >
                    <Chip size="small" label="이관 제외" color="error" variant="outlined" />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#5f6368', fontSize: 13 }}>
                    {row.migrationNote}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const selectControlSx = {
    flex: { xs: '1 1 100%', sm: '1 1 auto' },
    minWidth: { sm: 240 },
    maxWidth: { sm: 360 },
  };

  const formatComboLabel = (opt) => {
    const name = String(opt?.name ?? '').trim();
    const value = String(opt?.value ?? '').trim();
    if (name && value) return `${name} (${value})`;
    return name || value;
  };

  const renderComboSelect = (
    label,
    field,
    options,
    disabled,
    onChange
  ) => (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled || !options?.length}
      sx={{ mb: 2 }}
    >
      <InputLabel id={`dat-basic-${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`dat-basic-${field}-label`}
        label={label}
        value={inputs[field] || ''}
        onChange={(e) => {
          const value = e.target.value;
          if (onChange) onChange(value);
          else props.HandleInputChange(field)(value);
        }}
      >
        {options?.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {formatComboLabel(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderDepSelect = (label, field, options, disabled) => (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled || !options?.length}
      sx={{ mt: 2 }}
    >
      <InputLabel id={`dat-basic-${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`dat-basic-${field}-label`}
        label={label}
        value={inputs[field] || ''}
        onChange={(e) => props.HandleInputChange(field)(e.target.value)}
      >
        {options?.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {formatComboLabel(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderCodePanel = (
    title,
    description,
    prefix,
    custList,
    onCoChange,
    depLabel,
    depField,
    depOptions
  ) => (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        bgcolor: '#fff',
        minWidth: 0,
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: '#111418', mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#5f6368', mb: 3 }}>
        {description}
      </Typography>

      {renderComboSelect(
        '회사',
        `${prefix}CoCd`,
        props.coList,
        !props.coList?.length,
        onCoChange
      )}
      {renderComboSelect(
        '사업장',
        `${prefix}CustCd`,
        custList,
        !inputs[`${prefix}CoCd`] || !custList?.length
      )}
      {!inputs[`${prefix}CoCd`] && (
        <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', mb: 1 }}>
          회사를 먼저 선택하세요.
        </Typography>
      )}
      {inputs[`${prefix}CoCd`] && !custList?.length && (
        <Typography variant="caption" sx={{ color: '#c62828', display: 'block', mb: 1 }}>
          선택한 회사에 등록된 사업장이 없습니다.
        </Typography>
      )}

      {showSamDepMapping &&
        renderDepSelect(
          depLabel,
          depField,
          depOptions,
          props.depLoading
        )}
      {showSamDepMapping && props.depLoading && (
        <Typography variant="caption" sx={{ color: '#1565c0', mt: 0.5, display: 'block' }}>
          부서 목록 불러오는 중…
        </Typography>
      )}
    </Paper>
  );

  return (
    <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid xs={4} md={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            bgcolor: '#f6f7f8',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            데이터 이관
          </Typography>
          <Typography variant="body2" sx={{ color: '#5f6368', mb: 2 }}>
            원본 회사·사업장 데이터를 대상으로 복사(INSERT)합니다.
          </Typography>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: '#111418',
                minWidth: { sm: 64 },
                flexShrink: 0,
              }}
            >
              이관 구분
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={inputs.migrationMode || DAT_BASIC_MIGRATION_MODE.PAGE}
              onChange={(_, value) => {
                if (value) props.HandleMigrationModeChange(value);
              }}
              sx={{ flexShrink: 0 }}
            >
              {/* <ToggleButton value={DAT_BASIC_MIGRATION_MODE.ALL}>
                전체 페이지
              </ToggleButton> */}
              <ToggleButton value={DAT_BASIC_MIGRATION_MODE.PAGE}>
                페이지 선택
              </ToggleButton>
            </ToggleButtonGroup>

            {isPageMode && (
              <>
                <FormControl size="small" sx={selectControlSx}>
                  <InputLabel id="dat-basic-pro-id-label">이관 페이지</InputLabel>
                  <Select
                    labelId="dat-basic-pro-id-label"
                    label="이관 페이지"
                    value={inputs.proId || ''}
                    onChange={(e) => props.HandleProIdChange(e.target.value)}
                  >
                    {migratablePages.map((page) => (
                      <MenuItem key={page.value} value={page.value}>
                        {page.name} ({page.value})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {showTabSelect && (
                  <FormControl size="small" sx={selectControlSx}>
                    <InputLabel id="dat-basic-tab-key-label">이관 탭</InputLabel>
                    <Select
                      labelId="dat-basic-tab-key-label"
                      label="이관 탭"
                      value={inputs.tabKey ?? ''}
                      onChange={(e) =>
                        props.HandleTabKeyChange(e.target.value)
                      }
                    >
                      {tabOptions.map((tab) => (
                        <MenuItem
                          key={tab.value || 'ALL'}
                          value={tab.value}
                        >
                          {tab.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Box>

          {isPageMode && renderMigrationGuidePanel()}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {renderCodePanel(
              '원본 (복사 기준)',
              showSamDepMapping
                ? '이관할 데이터가 있는 회사·사업장·부서를 선택하세요.'
                : '이관할 데이터가 있는 회사·사업장을 선택하세요.',
              'source',
              props.sourceCustList,
              props.HandleSourceCoChange,
              '원본 부서',
              'sourceDepCd',
              props.sourceDepOptions
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 0, md: 1 },
                py: { xs: 1, md: 0 },
              }}
            >
              <SwapHorizIcon sx={{ fontSize: 36, color: '#1976d2' }} />
            </Box>

            {renderCodePanel(
              '대상 (이관 위치)',
              showSamDepMapping
                ? '복사된 데이터가 들어갈 회사·사업장·부서를 선택하세요.'
                : '복사된 데이터가 들어갈 회사·사업장을 선택하세요.',
              'target',
              props.targetCustList,
              props.HandleTargetCoChange,
              '대상 부서',
              'targetDepCd',
              props.targetDepOptions
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              name="선택 페이지 이관"
              onClick={() => props.HandleCopyClick()}
              sx={{
                minWidth: 180,
                fontWeight: 700,
                bgcolor: '#1976d2',
                color: '#fff',
                '&:hover': { bgcolor: '#1565c0' },
              }}
            />
          </Box>

          {props.showBudBasicDelete && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                대상 예산 데이터 삭제
              </Typography>
              <Typography variant="body2" sx={{ color: '#c62828', mb: 2 }}>
                대상 사업장 {props.targetCustDisplay ?? '미입력'}의 예산 데이터를
                삭제합니다. 재이관 전 초기화용이며 복구할 수 없습니다.
              </Typography>
              <Typography variant="body2" sx={{ color: '#5f6368', mb: 2 }}>
                삭제 범위:{' '}
                {(props.budDeleteTargets ?? [])
                  .map((t) => t.label)
                  .join(' · ')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  name="대상 예산 데이터 삭제"
                  onClick={() => props.HandleDeleteClick()}
                  sx={{
                    minWidth: 200,
                    fontWeight: 700,
                    bgcolor: '#c62828',
                    color: '#fff',
                    '&:hover': { bgcolor: '#b71c1c' },
                  }}
                />
              </Box>
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default DAT_BASIC;
